/**
 * Gateway API React Query Hooks
 *
 * Custom hooks that use React Query with the gateway client for type-safe API calls.
 * These hooks handle loading states, error states, and caching automatically.
 * Uses TanStack Query's native patterns without custom context layers.
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import { useQueryClient } from '@tanstack/react-query';
import { tsrClient } from '../api/client';
import { serializeError } from '../utils/error-utils';
import { handleSessionErrorInline } from '../utils/inline-session-handler';

const logger = createLogger('client:hook:use-gateway-queries');
/**
 * Query Keys for consistent cache management
 * Updated to match current API contract structure
 */
export const gatewayQueryKeys = {
  health: ['gateway', 'health'] as const,
  auth: {
    me: ['gateway', 'auth', 'me'] as const,
    login: ['gateway', 'auth', 'login'] as const,
    callback: ['gateway', 'auth', 'callback'] as const,
    logout: ['gateway', 'auth', 'logout'] as const,
    widget: ['gateway', 'auth', 'widget'] as const,
    refreshUser: ['gateway', 'auth', 'refreshUser'] as const,
  },
  ai: {
    askExpert: ['gateway', 'ai', 'askExpert'] as const,
    generateSuggestions: ['gateway', 'ai', 'generateSuggestions'] as const,
  },
  fileUpload: {
    uploadToOpenAI: ['gateway', 'fileUpload', 'uploadToOpenAI'] as const,
  },
  title: {
    generateTitle: ['gateway', 'title', 'generateTitle'] as const,
  },
  billing: {
    subscription: ['gateway', 'billing', 'subscription'] as const,
    paymentMethods: ['gateway', 'billing', 'paymentMethods'] as const,
    billingIntent: ['gateway', 'billing', 'billingIntent'] as const,
    checkRecentPayment: ['gateway', 'billing', 'checkRecentPayment'] as const,
  },
  plans: {
    all: ['gateway', 'plans'] as const,
  },
  pandadoc: {
    createBAASession: ['gateway', 'pandadoc', 'createBAASession'] as const,
    baaStatus: ['gateway', 'pandadoc', 'baaStatus'] as const,
    userBAAStatus: ['gateway', 'pandadoc', 'userBAAStatus'] as const,
  },
  integrations: {
    verify: ['gateway', 'integrations', 'verify'] as const,
  },
  personalization: {
    user: ['gateway', 'personalization', 'user'] as const,
    org: ['gateway', 'personalization', 'org'] as const,
  },
  chatHistory: {
    chats: ['gateway', 'chatHistory', 'chats'] as const,
  },
  feedback: {
    features: ['gateway', 'feedback', 'features'] as const,
  },
  promptLibrary: {
    userPrompts: ['gateway', 'promptLibrary', 'userPrompts'] as const,
    userPreferences: ['gateway', 'promptLibrary', 'userPreferences'] as const,
  },
  tts: {
    synthesize: ['gateway', 'tts', 'synthesize'] as const,
  },
  aiSkills: {
    list: ['gateway', 'aiSkills', 'list'] as const,
  },
  tasks: {
    list: ['gateway', 'tasks', 'list'] as const,
    forClaims: (claimIds: string[]) =>
      ['gateway', 'tasks', 'forClaims', claimIds.join(',')] as const,
    comments: (taskId: string) => ['gateway', 'tasks', 'comments', taskId] as const,
    claimSummary: (claimId: string) => ['gateway', 'tasks', 'claimSummary', claimId] as const,
  },
} as const;

export const useAuthMeQuery = () => {
  return tsrClient.auth.me.useQuery({
    queryKey: gatewayQueryKeys.auth.me,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: (failureCount: number) => {
      // Don't retry auth errors
      return failureCount < 2;
    },
  });
};

/**
 * Enter Tasks — the current user's open Enter RCM worklist.
 * GET /tasks → { tasks: EnterTask[]; integration: 'connected' | 'none' }
 */
export const useTasksQuery = (scope: 'my' | 'all' = 'my', enabled = true, accountId?: string) => {
  return tsrClient.tasks.list.useQuery({
    // accountId is part of the KEY, not just the request. Without it, switching
    // practice would serve the previous practice's cached worklist until the
    // entry went stale — the same wrong-data symptom this change exists to fix.
    queryKey: [...gatewayQueryKeys.tasks.list, scope, accountId ?? 'all'],
    queryData: { query: { scope, ...(accountId ? { accountId } : {}) } },
    enabled,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
    // Background poll so a task assigned/closed in Enter shows up without a
    // manual reload. Only runs while the Tasks view is mounted (`enabled`) and
    // pauses when the tab is unfocused (refetchIntervalInBackground defaults to
    // false), so it stays cheap.
    refetchInterval: 1000 * 60,
    retry: (failureCount: number, error: unknown) => {
      // Don't retry auth errors.
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Enter Tasks for a patient's claims — the RTM Live Patient Snapshot list.
 * GET /tasks/for-claims?claimIds=<csv> → { tasks: EnterTask[] } (enriched with
 * assignee + category names). Disabled until there's at least one claim id.
 */
export const usePatientTasksQuery = (
  claimIds: string[],
  enabled = true,
  accountId?: string
) => {
  const claims = [...claimIds].sort();
  return tsrClient.tasks.listForClaims.useQuery({
    queryKey: [...gatewayQueryKeys.tasks.forClaims(claims), accountId ?? 'all'],
    queryData: {
      query: { claimIds: claims.join(','), ...(accountId ? { accountId } : {}) },
    },
    enabled: enabled && claims.length > 0,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
    retry: (failureCount: number, error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Record a manual patient payment against an Enter claim.
 * POST /payments → { payment: RecordedPayment }
 *
 * Invalidates the Real-Time Mode snapshot is NOT done here: the balance shown
 * comes from Enter and may lag a posted payment, so we let the next natural
 * refresh pick it up rather than implying an instant ledger update.
 */
export const useRecordPaymentMutation = () => {
  return tsrClient.payments.record.useMutation({
    retry: false, // money: never auto-retry — a retry could double-post.
    onError: (error: unknown) => {
      logger.error('❌ [RECORD-PAYMENT] failed:', serializeError(error));
    },
  });
};

/** Existing payments on a patient's claims — powers the refund picker. */
export const usePaymentsQuery = (accountId: string, claimIds: string[], enabled = true) => {
  const claims = [...claimIds].sort();
  return tsrClient.payments.list.useQuery({
    queryKey: ['gateway', 'payments', 'list', accountId, claims.join(',')] as const,
    queryData: { query: { accountId, claimIds: claims.join(',') } },
    enabled: enabled && !!accountId && claims.length > 0,
    staleTime: 1000 * 15,
    retry: false,
  });
};

/** Refund (part of) an existing payment. */
export const useRefundPaymentMutation = () => {
  const queryClient = useQueryClient();
  return tsrClient.payments.refund.useMutation({
    retry: false, // money: never auto-retry — a retry could double-refund.
    onSuccess: () => {
      // Refundable balances just changed — drop the picker's cache.
      queryClient.invalidateQueries({ queryKey: ['gateway', 'payments', 'list'] });
    },
    onError: (error: unknown) => {
      logger.error('❌ [REFUND-PAYMENT] failed:', serializeError(error));
    },
  });
};

/**
 * Close an Enter task (→ closes the underlying Enter message). Invalidates the
 * task list on success so the closed task drops off.
 */
export const useCloseTaskMutation = () => {
  const queryClient = useQueryClient();
  return tsrClient.tasks.close.useMutation({
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.tasks.list });
    },
    onError: (error: unknown) => {
      logger.error('❌ [CLOSE-TASK] failed:', serializeError(error));
    },
  });
};

/**
 * Patient identity for a task's claim — name + MRN.
 *
 * Lazy by design: fetched when a task is OPENED, not for every row. Jordan's
 * ask is about finding the record in the EMR to fix a task, which happens in the
 * detail panel — doing it per row would be N upstream calls for information
 * nobody has asked to see yet.
 *
 * Cached long-ish: a patient's MRN does not change while you work the task.
 */
export const useClaimSummaryQuery = (accountId: string, claimId: string, enabled = true) =>
  tsrClient.tasks.claimSummary.useQuery({
    queryKey: gatewayQueryKeys.tasks.claimSummary(claimId),
    queryData: { query: { accountId, claimId } },
    enabled: enabled && !!accountId && !!claimId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

/**
 * A task's comments — read ONLY to recover which resolve action already ran, so
 * the Resolved card survives a reload.
 *
 * `staleTime: 0` on purpose: this is checked when a panel opens, right after a
 * resolve may have been posted from another surface. A cached "not resolved yet"
 * would re-offer an action that has already been taken.
 */
export const useTaskCommentsQuery = (taskId: string, accountId: string, enabled = true) =>
  tsrClient.tasks.comments.useQuery({
    queryKey: gatewayQueryKeys.tasks.comments(taskId),
    queryData: { params: { id: taskId }, query: { accountId } },
    enabled: enabled && !!taskId && !!accountId,
    staleTime: 0,
  });

/**
 * Post a comment on a task — also the RESOLVE mechanism, since Enter has no
 * resolve endpoint and acts on sentinel comment bodies instead.
 *
 * `retry: false` for the same reason the payment mutations use it: a retried
 * resolve action posts the sentinel twice, and "I've updated my EMR" twice is a
 * second claim re-check we never asked for.
 */
export const useTaskCommentMutation = () => {
  const queryClient = useQueryClient();
  return tsrClient.tasks.comment.useMutation({
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.tasks.list });
      // The thread just changed, and the Resolved card is derived from it.
      queryClient.invalidateQueries({ queryKey: ['gateway', 'tasks', 'comments'] });
    },
    onError: (error: unknown) => {
      logger.error('❌ [TASK-COMMENT] failed:', serializeError(error));
    },
  });
};

/**
 * Get Current User Query
 *
 * Gets the current authenticated user from the server.
 * Uses the /auth/me endpoint to validate session.
 */
export const useCurrentUserQuery = () => {
  return tsrClient.auth.me.useQuery({
    queryKey: gatewayQueryKeys.auth.me,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: (failureCount: number, error: unknown) => {
      // Don't retry auth errors
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Login Query
 *
 * Initiates the OAuth login flow by getting the authorization URL.
 * Returns a redirect URL that should be opened in a browser.
 * AuthKit will show all configured social login providers (Google, Microsoft) in the interface.
 */
export const useLoginQuery = () => {
  return tsrClient.auth.login.useQuery({
    queryKey: gatewayQueryKeys.auth.login,
    enabled: false, // Manual trigger only
    staleTime: 0, // Always fresh
    retry: false,
  });
};

/**
 * Auth Callback Query
 *
 * Handles the OAuth callback with the authorization code.
 * Exchanges the code for user session and returns user data.
 */
export const useAuthCallbackQuery = (code: string) => {
  return tsrClient.auth.callback.useQuery({
    queryKey: [...gatewayQueryKeys.auth.callback, code],
    queryData: { query: { code } },
    enabled: !!code, // Only run when we have a code
    staleTime: 0, // Always fresh
    retry: false,
  });
};

/**
 * Logout Mutation
 *
 * Logs out the current user by clearing their session.
 * Requires the userId to be provided.
 */
export const useLogoutMutation = () => {
  return tsrClient.auth.logout.useMutation({
    retry: false,
  });
};

/**
 * Refresh User Mutation
 *
 * Refreshes the current user data including role and permissions
 * without requiring a full re-authentication. Useful when user
 * roles have been updated in WorkOS.
 */
export const useRefreshUserMutation = () => {
  return tsrClient.auth.refreshUser.useMutation({
    retry: false,
  });
};

/**
 * Ask Expert Mutation
 *
 * Sends a request to the AI expert endpoint with optional file attachments.
 * Returns a streaming response for real-time chat.
 * Uses ts-rest mutation pattern.
 */
export const useAskExpertMutation = () => {
  return tsrClient.ai.askExpert.useMutation({
    retry: false, // Don't retry streaming requests
  });
};

/**
 * Helper hook for streaming AI responses
 *
 * Handles the ReadableStream response from askExpert and provides
 * a convenient interface for consuming the stream.
 */
export const useStreamingResponse = () => {
  const processStream = async (
    stream: ReadableStream,
    onChunk: (chunk: string) => void,
    onComplete?: () => void,
    onError?: (error: Error) => void
  ) => {
    try {
      const reader = stream.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          onComplete?.();
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        onChunk(chunk);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Stream processing failed');
      onError?.(err);
    }
  };

  return { processStream };
};

/**
 * File Upload Hooks
 */

/**
 * Upload File to OpenAI Mutation
 *
 * Uploads a file to OpenAI and returns the file ID for use in vision tasks.
 * Supports multipart form data with optional metadata.
 */
export const useUploadToOpenAIMutation = () => {
  return tsrClient.fileUpload.convertToBase64.useMutation({
    retry: false, // Don't retry file uploads
  });
};

/**
 * Title Generation Hooks
 */

/**
 * Generate Title Mutation
 *
 * Generates a concise title for a chat session based on the first message.
 * Uses AI to create meaningful titles for better organization.
 */
export const useGenerateTitleMutation = () => {
  return tsrClient.ai.generateTitle.useMutation({
    retry: false,
  });
};

/**
 * Generate Token Mutation
 *
 * Generates a token for the user.
 */
export const useGenerateTokenQuery = () => {
  return tsrClient.auth.widget.useQuery({
    queryKey: [...gatewayQueryKeys.auth.widget],
    enabled: false,
    retry: false,
  });
};

/**
 * Billing Hooks
 */

/**
 * Get User Subscription Query
 *
 * Fetches the user's current subscription and available plans.
 * Requires authentication.
 */
export const useUserSubscriptionQuery = (userId?: string) => {
  return tsrClient.billing.getUserSubscription.useQuery({
    queryKey: [...gatewayQueryKeys.billing.subscription, userId],
    enabled: !!userId, // Only run when we have userId
    staleTime: 1000 * 30, // 30 seconds - much shorter cache time for subscription changes
    retry: (failureCount: number, error: unknown) => {
      // Handle session invalid errors
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        logger.info('Error in subscription query - auth error:', error);
        handleSessionErrorInline(error).catch(console.error);
        return false; // Don't retry auth errors
      }
      return failureCount < 2;
    },
  });
};

/**
 * Update Subscription Mutation
 *
 * Creates, updates, or cancels a user subscription.
 * Uses ts-rest mutation pattern.
 */
export const useUpdateSubscriptionMutation = () => {
  return tsrClient.billing.updateSubscription.useMutation({
    retry: false,
  });
};

/**
 * Create Checkout Session Mutation
 *
 * Creates a checkout session for the logged in WorkOS organization's stripe customer.
 * Returns a customer-session-client-secret that can be used with the Stripe Pricing Table.
 */
export const useCreateCheckoutSessionMutation = () => {
  const mutation = tsrClient.billing.createCheckoutSession.useMutation({
    retry: false,
  });

  // Return a modified mutation with a custom mutateAsync that ensures proper JSON stringification
  return {
    ...mutation,
    mutateAsync: async (variables: any) => {
      // Create a copy of the original fetch function
      const originalFetch = window.fetch;

      // Override fetch temporarily to handle this specific endpoint
      window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        if (
          typeof input === 'string' &&
          input.includes('/billing/checkout') &&
          init?.method === 'POST'
        ) {
          // Ensure we have proper headers
          const headers = new Headers(init.headers);
          headers.set('Content-Type', 'application/json');

          // Create new init with stringified body and updated headers
          const newInit: RequestInit = {
            ...init,
            headers,
            body: JSON.stringify(variables || {}),
          };

          // Call original fetch with modified init
          return originalFetch(input, newInit);
        }

        // For all other requests, use original fetch
        return originalFetch(input, init);
      };

      try {
        // Call the original mutateAsync
        const result = await mutation.mutateAsync(variables);
        return result;
      } finally {
        // Restore original fetch
        window.fetch = originalFetch;
      }
    },
  };
};

/**
 * Create Portal Session Mutation
 *
 * Creates a billing portal session for the logged in WorkOS organization's stripe customer.
 * Returns a URL that can be used to redirect the customer to the billing portal.
 */
export const useCreatePortalSessionMutation = () => {
  const mutation = tsrClient.billing.createPortalSession.useMutation({
    retry: false,
  });

  // Return a modified mutation with a custom mutateAsync that ensures proper JSON stringification
  return {
    ...mutation,
    mutateAsync: async (variables: any) => {
      // Create a copy of the original fetch function
      const originalFetch = window.fetch;

      // Override fetch temporarily to handle this specific endpoint
      window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        if (
          typeof input === 'string' &&
          input.includes('/billing/portal_session') &&
          init?.method === 'POST'
        ) {
          // Ensure we have proper headers
          const headers = new Headers(init.headers);
          headers.set('Content-Type', 'application/json');

          // Create new init with stringified body and updated headers
          const newInit: RequestInit = {
            ...init,
            headers,
            body: JSON.stringify(variables || {}),
          };

          // Call original fetch with modified init
          return originalFetch(input, newInit);
        }

        // For all other requests, use original fetch
        return originalFetch(input, init);
      };

      try {
        // Call the original mutateAsync
        const result = await mutation.mutateAsync(variables);
        return result;
      } finally {
        // Restore original fetch
        window.fetch = originalFetch;
      }
    },
  };
};

/**
 * Check Chat Access Query
 *
 * Checks if authenticated user has chat access based on organization subscription.
 * Works for both admin and regular members.
 */
// Track query failures to prevent infinite loops
let consecutiveFailures = 0;
let lastFailureTime = 0;

export const useCheckChatAccessQuery = (enabled = true) => {
  // Prevent query if we've had too many recent failures
  const now = Date.now();
  const shouldBlock = consecutiveFailures >= 3 && now - lastFailureTime < 30000; // Block for 30s after 3 failures

  if (shouldBlock && enabled) {
    logger.warn('🛑 [useCheckChatAccessQuery] Blocking query due to repeated failures');
  }

  const result = tsrClient.billing.checkChatAccess.useQuery({
    queryKey: ['gateway', 'billing', 'chatAccess'],
    enabled: enabled && !shouldBlock,
    staleTime: 1000 * 60 * 5, // 5 minutes - chat access doesn't change that often
    refetchOnWindowFocus: false, // ❌ DISABLED - prevents rerender loops
    refetchOnMount: false, // ❌ DISABLED - prevents infinite loop on 401 errors
    refetchInterval: false, // ❌ DISABLED - manual refresh only
    retry: (failureCount: number, error: unknown) => {
      // Handle session invalid errors
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        handleSessionErrorInline(error).catch(console.error);

        // Track consecutive failures
        consecutiveFailures++;
        lastFailureTime = Date.now();
        logger.warn(`⚠️ [useCheckChatAccessQuery] Consecutive failures: ${consecutiveFailures}`);

        return false; // Don't retry auth errors
      }

      // Success - reset failure counter
      if (failureCount === 0) {
        consecutiveFailures = 0;
      }

      return failureCount < 2;
    },
  });
  return result;
};

/**
 * Check Recent Payment Query
 *
 * Checks if the user recently completed a payment (within last 5 minutes).
 * Used after checkout window closes to determine if payment succeeded.
 */
export const useCheckRecentPaymentQuery = (enabled = false) => {
  const result = tsrClient.billing.checkRecentPayment.useQuery({
    queryKey: gatewayQueryKeys.billing.checkRecentPayment,
    enabled,
    staleTime: 0, // Always fresh - this is a one-time check
    retry: 1,
  });
  return result;
};

/**
 * Plans Hooks
 */

/**
 * Get Plans Query
 *
 * Fetches all available subscription plans.
 * This is a public endpoint that doesn't require authentication.
 */
export const usePlansQuery = (source?: string) => {
  return tsrClient.plans.getPlans.useQuery({
    queryKey: [...gatewayQueryKeys.plans.all, source],
    queryData: source ? { query: { source } } : { query: {} },
    staleTime: 1000 * 60 * 10, // 10 minutes - plans don't change often
    retry: 2,
  });
};

/**
 * PandaDoc Hooks
 */

/**
 * Create BAA Signing Session Mutation
 *
 * Creates a new BAA signing session for a user.
 * Returns document ID and session URL for external browser signing.
 * Completion is detected via PandaDoc webhook.
 */
export const useCreateBAASigningSessionMutation = () => {
  return tsrClient.pandadoc.createBAASigningSession.useMutation({
    retry: false, // Don't retry document creation
    onSuccess: data => {
      logger.info(
        '✅ [BAA-SESSION-MUTATION] Session created:',
        JSON.stringify(
          {
            documentId: data.body.data?.documentId,
            hasSessionUrl: !!data.body.data?.sessionUrl,
          },
          null,
          2
        )
      );
    },
    onError: error => {
      logger.error(
        '❌ [BAA-SESSION-MUTATION] Session creation failed:',
        JSON.stringify(
          {
            error,
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
          },
          null,
          2
        )
      );
    },
  });
};

/**
 * Get BAA Status Query
 *
 * Fetches the current status of a BAA document.
 * Used for manual status checks only - completion is detected via webhook.
 */
export const useBAAStatusQuery = (documentId: string, enabled = true) => {
  return tsrClient.pandadoc.getBAAStatus.useQuery({
    queryKey: [...gatewayQueryKeys.pandadoc.baaStatus, documentId],
    queryData: { params: { documentId } },
    enabled: enabled && !!documentId,
    staleTime: 1000 * 60 * 5, // 5 minutes - longer stale time since webhooks handle completion
    // No automatic polling - completion detected via webhook
    retry: (failureCount: number, error: unknown) => {
      // Don't retry 404 errors (document not found)
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Check User BAA Status Mutation
 *
 * Checks if a specific user has signed their BAA document.
 * Returns signing status and timestamp.
 */
export const useCheckUserBAAStatusMutation = () => {
  return tsrClient.pandadoc.checkUserBAAStatus.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info(
        '✅ [USER-BAA-STATUS-MUTATION] Status checked:',
        JSON.stringify(
          {
            hasSigned: data.body.data?.hasSigned,
            signedAt: data.body.data?.signedAt,
          },
          null,
          2
        )
      );
    },
    onError: error => {
      logger.error(
        '❌ [USER-BAA-STATUS-MUTATION] Status check failed:',
        JSON.stringify(
          {
            error,
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
          },
          null,
          2
        )
      );
    },
  });
};

// Note: baaWebhook mutation removed - webhooks are now handled server-side only
// PandaDoc webhooks are processed directly by the backend when recipient_completed events occur

/**
 * Integrations Hooks
 */

/**
 * Verify Integration Mutation
 *
 * Verifies ENTER Health API credentials by exchanging them for tokens
 * and retrieving organization information.
 * Returns organization details if credentials are valid.
 */
export const useVerifyIntegrationMutation = () => {
  return tsrClient.integrations.verifyIntegration.useMutation({
    retry: false, // Don't retry failed credential verification
    onSuccess: data => {
      logger.info(
        '✅ [VERIFY-INTEGRATION] Integration verified:',
        JSON.stringify(
          {
            success: data.body.success,
            organizationId: data.body.organization?.id,
            organizationName: data.body.organization?.name,
          },
          null,
          2
        )
      );
    },
    onError: error => {
      logger.error(
        '❌ [VERIFY-INTEGRATION] Verification failed:',
        JSON.stringify(
          {
            error,
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
          },
          null,
          2
        )
      );
    },
  });
};

/**
 * Save Integration Mutation
 *
 * Saves integration to WorkOS organization metadata.
 * Automatically invalidates integrations cache on success.
 */
export const useSaveIntegrationMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.integrations.saveIntegration.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('✅ [SAVE-INTEGRATION] Integration saved:', data.body.message);

      // Invalidate integrations query to force refetch
      queryClient.invalidateQueries({
        queryKey: [...gatewayQueryKeys.integrations.verify, 'list'],
      });
    },
    onError: error => {
      logger.error(
        '❌ [SAVE-INTEGRATION] Save failed:',
        JSON.stringify(
          {
            error,
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
          },
          null,
          2
        )
      );
    },
  });
};

/**
 * Get Integrations Query
 *
 * Retrieves all integrations from WorkOS organization metadata.
 */
export const useGetIntegrationsQuery = (enabled = true) => {
  return tsrClient.integrations.getIntegrations.useQuery({
    queryKey: [...gatewayQueryKeys.integrations.verify, 'list'],
    enabled,
    staleTime: 0, // Always fetch fresh data to handle edge cases
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
    retry: (failureCount: number, error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Delete Integration Mutation
 *
 * Deletes integration from WorkOS organization metadata.
 * Automatically invalidates integrations cache on success.
 */
export const useDeleteIntegrationMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.integrations.deleteIntegration.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('✅ [DELETE-INTEGRATION] Integration deleted:', data.body.message);

      // Invalidate integrations query to force refetch
      queryClient.invalidateQueries({
        queryKey: [...gatewayQueryKeys.integrations.verify, 'list'],
      });
    },
    onError: error => {
      logger.error(
        '❌ [DELETE-INTEGRATION] Delete failed:',
        JSON.stringify(
          {
            error,
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
          },
          null,
          2
        )
      );
    },
  });
};

/**
 * Get User Personalization Query
 *
 * Gets user's personalization settings from server
 * Note: For MVP, settings are stored client-side in Zustand
 * This query exists for future server-side persistence
 */
export const useUserPersonalizationQuery = (enabled = true) => {
  return tsrClient.personalization.getUserPersonalization.useQuery({
    queryKey: gatewayQueryKeys.personalization.user,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
    enabled,
    // Note: Backend always returns empty {} for user settings
    // User settings are stored client-side only via Zustand persist middleware
  });
};

/**
 * Update User Personalization Mutation
 *
 * Updates user's personalization settings
 * Note: For MVP, actual persistence happens client-side via Zustand
 */
export const useUpdateUserPersonalizationMutation = () => {
  return tsrClient.personalization.updateUserPersonalization.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('✅ [UPDATE-USER-PERSONALIZATION] Settings updated:', data.body);
    },
    onError: error => {
      logger.error(
        '❌ [UPDATE-USER-PERSONALIZATION] Update failed:',
        JSON.stringify(
          {
            error: error instanceof Error ? error.message : error,
            errorStack: error instanceof Error ? error.stack : undefined,
          },
          null,
          2
        )
      );
      logger.error('❌ [UPDATE-USER-PERSONALIZATION] Raw error:', error);
    },
  });
};

/**
 * Get Organization Personalization Query
 *
 * Gets organization's personalization settings from WorkOS metadata
 * Admin-only endpoint
 */
export const useOrgPersonalizationQuery = (enabled = true) => {
  return tsrClient.personalization.getOrgPersonalization.useQuery({
    queryKey: gatewayQueryKeys.personalization.org,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled, // Can be disabled for non-admins
    retry: (failureCount: number, error: unknown) => {
      // Don't retry 403 errors (non-admin)
      if (error && typeof error === 'object' && 'status' in error && error.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Update Organization Personalization Mutation
 *
 * Updates organization's personalization settings in WorkOS metadata
 * Admin-only endpoint
 */
export const useUpdateOrgPersonalizationMutation = () => {
  return tsrClient.personalization.updateOrgPersonalization.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('✅ [UPDATE-ORG-PERSONALIZATION] Organization settings updated:', data.body);
    },
    onError: error => {
      logger.error('❌ [UPDATE-ORG-PERSONALIZATION] Update failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

/**
 * Update Organization Name Mutation
 *
 * Updates organization's name in WorkOS
 * Admin-only endpoint
 */
export const useUpdateOrganizationNameMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.personalization.updateOrganizationName.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('✅ [UPDATE-ORG-NAME] Organization name updated:', data.body);

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.billing.subscription });
      queryClient.invalidateQueries({ queryKey: ['gateway', 'billing', 'chat-access'] });
    },
    onError: error => {
      logger.error('❌ [UPDATE-ORG-NAME] Update failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

/**
 * Chat History Mutations
 */

/**
 * Archive Chat Mutation
 *
 * Archives a chat session
 */
export const useArchiveChatMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.chatHistory.archiveChat.useMutation({
    retry: false,
    onSuccess: (data, variables) => {
      logger.info('📦 [ARCHIVE-CHAT] Chat archived:', variables.params.sessionId);
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.chatHistory.chats });
    },
    onError: error => {
      logger.error('❌ [ARCHIVE-CHAT] Archive failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

/**
 * Unarchive Chat Mutation
 *
 * Unarchives a chat session
 */
export const useUnarchiveChatMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.chatHistory.unarchiveChat.useMutation({
    retry: false,
    onSuccess: (data, variables) => {
      logger.info('📤 [UNARCHIVE-CHAT] Chat unarchived:', variables.params.sessionId);
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.chatHistory.chats });
    },
    onError: error => {
      logger.error('❌ [UNARCHIVE-CHAT] Unarchive failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

/**
 * Delete Chat Mutation
 *
 * Permanently deletes a chat session
 */
export const useDeleteChatMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.chatHistory.deleteChat.useMutation({
    retry: false,
    onSuccess: (data, variables) => {
      logger.info('🗑️ [DELETE-CHAT] Chat deleted:', variables.params.sessionId);
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.chatHistory.chats });
    },
    onError: error => {
      logger.error('❌ [DELETE-CHAT] Delete failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

/**
 * Update Chat Title Mutation
 *
 * Updates a chat session's title
 */
export const useUpdateChatTitleMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.chatHistory.updateChatTitle.useMutation({
    retry: false,
    onSuccess: (data, variables) => {
      logger.info(
        '✏️ [UPDATE-CHAT-TITLE] Title updated:',
        variables.params.sessionId,
        '->',
        variables.body.title
      );
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.chatHistory.chats });
    },
    onError: error => {
      logger.error('❌ [UPDATE-CHAT-TITLE] Update failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

/**
 * Get Features Query
 *
 * Gets all features and user's feedback status
 */
export const useFeaturesQuery = () => {
  return tsrClient.feedback.getFeatures.useQuery({
    queryKey: gatewayQueryKeys.feedback.features,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
  });
};

/**
 * Submit Feedback Mutation
 *
 * Submits user feedback for a feature
 */
export const useSubmitFeedbackMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.feedback.submitFeedback.useMutation({
    retry: false,
    onSuccess: () => {
      logger.info('💬 [SUBMIT-FEEDBACK] Feedback submitted successfully');
      // Invalidate features query to refresh feedback status
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.feedback.features });
    },
    onError: error => {
      logger.error('❌ [SUBMIT-FEEDBACK] Submit failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

/**
 * Dismiss Features Mutation
 *
 * Dismisses features (user clicked "Remind me later")
 */
export const useDismissFeaturesMutation = () => {
  return tsrClient.feedback.dismissFeatures.useMutation({
    retry: false,
    onSuccess: (data, variables) => {
      logger.info('⏭️ [DISMISS-FEATURES] Features dismissed:', variables.body.featureIds);
    },
    onError: error => {
      logger.error('❌ [DISMISS-FEATURES] Dismiss failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

// ============================================================================
// PROMPT LIBRARY QUERIES & MUTATIONS
// ============================================================================

/**
 * Get User Prompts Query
 *
 * Gets user-specific prompt templates from WorkOS user metadata
 */
export const useUserPromptsQuery = (enabled = true) => {
  return tsrClient.promptLibrary.getUserPrompts.useQuery({
    queryKey: gatewayQueryKeys.promptLibrary.userPrompts,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
    retry: 2,
  });
};

/**
 * Create User Prompt Mutation
 *
 * Creates a new user-specific prompt template
 */
export const useCreateUserPromptMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.promptLibrary.createUserPrompt.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('✅ [CREATE-USER-PROMPT] Prompt created:', data.body.prompt);
      // Invalidate user prompts query to refresh
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.promptLibrary.userPrompts });
    },
    onError: error => {
      // Properly serialize error for logging
      const errorDetails = {
        errorType: typeof error,
        errorConstructor: error?.constructor?.name,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorBody: (error as any)?.body,
        errorStatus: (error as any)?.status,
        fullError: JSON.stringify(error, Object.getOwnPropertyNames(error)),
      };

      logger.error('❌ [CREATE-USER-PROMPT] Create failed:', errorDetails);
      logger.error('❌ [CREATE-USER-PROMPT] Raw error body:', (error as any)?.body);
    },
  });
};

/**
 * Update User Prompt Mutation
 *
 * Updates an existing user-specific prompt template
 */
export const useUpdateUserPromptMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.promptLibrary.updateUserPrompt.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('✅ [UPDATE-USER-PROMPT] Prompt updated:', data.body.prompt);
      // Invalidate user prompts query to refresh
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.promptLibrary.userPrompts });
    },
    onError: error => {
      logger.error('❌ [UPDATE-USER-PROMPT] Update failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

/**
 * Delete User Prompt Mutation
 *
 * Deletes a user-specific prompt template
 */
export const useDeleteUserPromptMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.promptLibrary.deleteUserPrompt.useMutation({
    retry: false,
    onSuccess: (data, variables) => {
      logger.info('✅ [DELETE-USER-PROMPT] Prompt deleted:', variables.params.promptId);
      // Invalidate user prompts query to refresh
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.promptLibrary.userPrompts });
    },
    onError: error => {
      logger.error('❌ [DELETE-USER-PROMPT] Delete failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

/**
 * Get User Preferences Query
 *
 * Gets user-specific preferences (favorites, usage, recently used)
 */
export const useUserPreferencesQuery = (enabled = true) => {
  return tsrClient.promptLibrary.getUserPreferences.useQuery({
    queryKey: gatewayQueryKeys.promptLibrary.userPreferences,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
    retry: 2,
  });
};

/**
 * Update User Preferences Mutation
 *
 * Updates user-specific preferences to sync across devices
 */
export const useUpdateUserPreferencesMutation = () => {
  const queryClient = useQueryClient();

  return tsrClient.promptLibrary.updateUserPreferences.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('✅ [UPDATE-USER-PREFERENCES] Preferences synced:', data.body.preferences);
      // Invalidate preferences query to refresh
      queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.promptLibrary.userPreferences });
    },
    onError: error => {
      logger.error('❌ [UPDATE-USER-PREFERENCES] Sync failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

// ============================================================================
// AI SUGGESTIONS MUTATION
// ============================================================================

/**
 * Generate AI Suggestions Mutation
 *
 * Generates AI-powered prompt suggestions based on context
 */
export const useGenerateSuggestionsMutation = () => {
  return tsrClient.ai.generateSuggestions.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('💡 [GENERATE-SUGGESTIONS] Generated suggestions:', data.body.suggestions.length);
    },
    onError: error => {
      logger.error('❌ [GENERATE-SUGGESTIONS] Generation failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

// ============================================================================
// OPENAI TEXT-TO-SPEECH MUTATIONS
// ============================================================================

/**
 * Synthesize TTS Mutation
 *
 * Converts text to speech using OpenAI gpt-4o-mini-tts and returns base64 MP3 audio
 * HIPAA compliant - no data storage
 */
export const useSynthesizeTTSMutation = () => {
  return tsrClient.tts.synthesize.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('🔊 [TTS] Audio generated:', {
        format: data.body.format,
        sizeKB: (data.body.sizeBytes / 1024).toFixed(2),
      });
    },
    onError: error => {
      logger.error('❌ [TTS] Synthesis failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

// ============================================================================
// CONTEXT COMPACTION MUTATIONS
// ============================================================================

/**
 * Compact Context Mutation
 *
 * Calls the OpenAI Responses Compact API via the gateway to compress conversation
 * history into an opaque token. Returns the token and the message index from which
 * new messages should be counted.
 */
export const useCompactContextMutation = () => {
  return tsrClient.ai.compactContext.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('🗜️ [CompactContext] Context compacted successfully:', {
        compactionMessageIndex: data.body.compactionMessageIndex,
      });
    },
    onError: error => {
      logger.error('❌ [CompactContext] Compaction failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

// ============================================================================
// OPENAI SPEECH-TO-TEXT MUTATIONS
// ============================================================================

/**
 * Transcribe STT Mutation
 *
 * Converts audio to text using OpenAI whisper-1 model
 * HIPAA compliant - no data storage
 */
export const useTranscribeSTTMutation = () => {
  return tsrClient.stt.transcribe.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('🎤 [STT] Audio transcribed:', {
        textLength: data.body.text.length,
        language: data.body.language || 'auto-detected',
      });
    },
    onError: error => {
      logger.error('❌ [STT] Transcription failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
};

// ============================================================================
// AI SKILLS QUERIES
// ============================================================================

/**
 * AI Skills Query
 *
 * Fetches the list of available AI specialist skills from the gateway.
 * The response omits server-only fields (systemPrompt, outputFormatPrompt).
 * Skills are relatively static — staleTime is set to 5 minutes.
 */
export const useAiSkillsQuery = () => {
  return tsrClient.aiSkills.listSkills.useQuery({
    queryKey: ['gateway', 'aiSkills', 'list'] as const,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    onSuccess: (data: { body: { skills: unknown[] } }) => {
      logger.info('🎯 [useAiSkillsQuery] Fetched skills:', {
        count: data.body?.skills?.length ?? 0,
      });
    },
    onError: (error: unknown) => {
      logger.error('❌ [useAiSkillsQuery] Failed to fetch skills:', error);
    },
  });
};

// ============================================================================
// Bug Report Mutations
// ============================================================================

/**
 * Submit Bug Report Mutation
 *
 * Submits comprehensive bug report with system info, console logs, and optional screenshot
 * Data sent to Sentry for tracking and analysis
 */
export const useSubmitBugReportMutation = () => {
  return tsrClient.bugReport.submitBugReport.useMutation({
    retry: false,
    onSuccess: data => {
      logger.info('🐛 [BUG-REPORT] Report submitted successfully:', {
        sentryEventId: data.body.sentryEventId,
        reportId: data.body.reportId,
      });
    },
    onError: error => {
      // Use error utilities for proper serialization
      const errorDetails = serializeError(error);

      // Log with multiple formats to bypass Sentry's console wrapping
      logger.error('❌ [BUG-REPORT] Submission failed');
      logger.info('❌ [BUG-REPORT] Error details:', errorDetails);
      logger.info('❌ [BUG-REPORT] Error JSON:', JSON.stringify(errorDetails, null, 2));
    },
  });
};

// ============================================================================
// Admin — Team Management
// ============================================================================

/**
 * List org members and pending invitations (admin only).
 */
export const useAdminTeamMembersQuery = (enabled = true) => {
  return tsrClient.admin.listMembers.useQuery({
    queryKey: ['gateway', 'admin', 'team', 'members'] as const,
    enabled,
    staleTime: 1000 * 30,
    retry: 1,
  });
};

/**
 * Remove an org member and sync Stripe seat count (admin only).
 */
export const useAdminRemoveMemberMutation = () => {
  const queryClient = useQueryClient();
  return tsrClient.admin.removeMember.useMutation({
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway', 'admin', 'team', 'members'] });
      queryClient.invalidateQueries({ queryKey: ['gateway', 'billing', 'chatAccess'] });
    },
    onError: (error: unknown) => {
      logger.error('❌ [useAdminRemoveMemberMutation] Failed:', error);
    },
  });
};

/**
 * Update a member role (admin ↔ member, admin only).
 */
export const useAdminUpdateMemberRoleMutation = () => {
  const queryClient = useQueryClient();
  return tsrClient.admin.updateMemberRole.useMutation({
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway', 'admin', 'team', 'members'] });
    },
    onError: (error: unknown) => {
      logger.error('❌ [useAdminUpdateMemberRoleMutation] Failed:', error);
    },
  });
};

/**
 * Invite a new member to the org by email (admin only).
 */
export const useAdminInviteMemberMutation = () => {
  const queryClient = useQueryClient();
  return tsrClient.admin.inviteMember.useMutation({
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway', 'admin', 'team', 'members'] });
    },
    onError: (error: unknown) => {
      logger.error('❌ [useAdminInviteMemberMutation] Failed:', error);
    },
  });
};

/**
 * Revoke a pending invitation (admin only).
 */
export const useAdminRevokeInvitationMutation = () => {
  const queryClient = useQueryClient();
  return tsrClient.admin.revokeInvitation.useMutation({
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway', 'admin', 'team', 'members'] });
    },
    onError: (error: unknown) => {
      logger.error('❌ [useAdminRevokeInvitationMutation] Failed:', error);
    },
  });
};

/**
 * Resend a pending invitation (admin only). Revokes the stale invite and sends a fresh email.
 */
export const useAdminResendInvitationMutation = () => {
  const queryClient = useQueryClient();
  return tsrClient.admin.resendInvitation.useMutation({
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway', 'admin', 'team', 'members'] });
    },
    onError: (error: unknown) => {
      logger.error('❌ [useAdminResendInvitationMutation] Failed:', error);
    },
  });
};

/**
 * Fetch per-user usage statistics for the org (admin only).
 */
export const useAdminUsageQuery = (enabled = true) => {
  return tsrClient.admin.getUsage.useQuery({
    queryKey: ['gateway', 'admin', 'usage'] as const,
    enabled,
    staleTime: 1000 * 60,
    retry: 1,
  });
};

/**
 * Fetch per-user daily usage time-series for the org (admin only).
 * `days` is passed as a query param (1–90, default 30).
 * `endDate` is an optional inclusive YYYY-MM-DD window end (defaults to today).
 */
export const useAdminUsageTimeseriesQuery = (
  days: number = 30,
  endDate?: string,
  enabled = true
) => {
  return tsrClient.admin.getUsageTimeseries.useQuery({
    queryKey: ['gateway', 'admin', 'usage', 'timeseries', days, endDate ?? 'today'] as const,
    queryData: { query: { days, ...(endDate ? { endDate } : {}) } },
    enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes — timeseries is more expensive to fetch
    retry: 1,
  });
};

/**
 * Fetch full per-user detail for the admin dashboard modal.
 * Enabled only when `userId` is truthy.
 * Activity trend chart uses useAdminUsageTimeseriesQuery separately.
 */
export const useAdminUserDetailQuery = (userId?: string | null) => {
  return tsrClient.admin.getUserDetail.useQuery({
    queryKey: ['gateway', 'admin', 'user-detail', userId ?? ''] as const,
    queryData: {
      params: { userId: userId ?? '' },
      query: { days: 30 },
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });
};

